import React from 'react';
import { Alert, Box } from '@mui/material';
import MainLayout from '../../../shared/ui/layout/MainLayout';
import WorkflowFooter from '../../../shared/ui/workflow/WorkflowFooter';
import { WorkflowStepShell } from '../../../shared/ui/workflow';
import { WorkspaceSpotlightTour } from '../../../shared/ui/common';
import QuickFillWorkspace from './QuickFillWorkspace';
import QuickFillDialogShell from './QuickFillDialogShell';
import { useQuickFillPagePropComposition } from './useQuickFillPagePropComposition';
import type { QuickFillPageValidationController } from '../hooks/useQuickFillPageValidationController';

interface QuickFillPageViewProps {
  controller: QuickFillPageValidationController;
  onGoBack: () => void;
  onContinue: () => void;
  embedded: boolean;
}

const QuickFillPageView: React.FC<QuickFillPageViewProps> = ({
  controller: c,
  onGoBack,
  onContinue,
  embedded,
}) => {
  const composition = useQuickFillPagePropComposition(c, onContinue);
  const renderInShell = (content: React.ReactNode) => (
    <Box
      data-testid="quickfill-content-shell"
      sx={{
        flex: 1,
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'transparent',
      }}
    >
      {embedded && !c.hostRibbonAvailable ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5, minHeight: 40 }}>
          {composition.utilityChromeElement}
        </Box>
      ) : null}
      {content}
    </Box>
  );

  if (c.combinedError) {
    const content = <Alert severity="error" sx={{ mb: 2 }}>Error: {c.combinedError}</Alert>;
    if (embedded) return renderInShell(content);
    return (
      <MainLayout
        title="Quick Fill properties"
        showBack
        onBack={onGoBack}
        disableContentScroll
        workflowChromeVariant="workspace"
        showRuntimeBadgeInUtilityChrome={false}
      >
        {renderInShell(content)}
      </MainLayout>
    );
  }

  const wrappedContent = (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
      <WorkflowStepShell sx={{ height: '100%', minHeight: 0, gap: { xs: 0.5, md: 0.75 }, pb: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <QuickFillWorkspace {...composition.workspaceProps} />
        </Box>
      </WorkflowStepShell>
      <QuickFillDialogShell {...composition.dialogProps} />
      <WorkspaceSpotlightTour
        open={c.tour.tourOpen}
        steps={c.quickFillTourSteps}
        stepIndex={c.tour.tourStep}
        onBack={() => c.tour.setTourStep((current) => Math.max(0, current - 1))}
        onNext={() => c.tour.setTourStep(
          (current) => Math.min(c.quickFillTourSteps.length - 1, current + 1)
        )}
        onJumpToStep={c.tour.setTourStep}
        onClose={c.tour.handleCloseTour}
        dialogLabel="Quick Fill workspace tour"
        chromeTargets={c.hostRibbonAvailable ? c.quickFillHostChromeTargets : undefined}
        showArrows
      />
    </Box>
  );

  if (embedded) return renderInShell(wrappedContent);
  return (
    <MainLayout
      title="Quick Fill properties"
      showBack
      onBack={c.guardedGoBack}
      showNext
      showSecondaryAction
      onSecondaryAction={c.submit.handleApplyClick}
      secondaryActionText="Apply"
      secondaryActionDisabled={c.isApplyDisabled || c.submit.isContinuing}
      secondaryActionAttention={c.quickFillHasPendingChanges}
      onNext={c.submit.handleContinueClick}
      nextText={c.submit.isContinuing ? 'Saving…' : 'Continue'}
      nextDisabled={c.isContinueDisabled}
      ariaLabel="Quick Fill properties configuration"
      disableContentScroll
      blockStepNavigation={c.isTableLoading || c.submit.isContinuing || c.operations.isSyncing}
      compactWorkflowChrome
      workflowChromeVariant="workspace"
      showRuntimeBadgeInUtilityChrome={false}
      hostRibbonActions={c.quickFillHostRibbonActions}
      utilityChromeAreaRef={c.refs.pageGuideClusterRef}
      utilityChrome={!c.hostRibbonAvailable ? composition.utilityChromeElement : null}
      footerContent={(
        <WorkflowFooter
          isReady={!c.isTableLoading && !c.submit.isContinuing && !c.operations.isSyncing}
          readyText="Workspace ready"
          blockedReasons={[
            c.submit.isContinuing || c.operations.isSyncing
              ? 'Quick Fill changes are still saving'
              : c.isTableLoading ? 'Quick Fill data is still loading' : '',
          ].filter(Boolean)}
          variant="workspace"
        />
      )}
      navigationActionsRef={c.refs.localFooterActionsRef}
      navigationActionsTestId="quickfill-tour-target-footer-actions"
    >
      {renderInShell(wrappedContent)}
    </MainLayout>
  );
};

export default QuickFillPageView;
